import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subscription, interval, startWith, switchMap } from 'rxjs';
import { INotification, NotificationsService } from '../../services/notifications.service';

/**
 * Top-bar notification bell (next to the profile chip). Carries PAYROLL REQUEST activity:
 * submissions notify the approver list (managed on the Payroll Requests Approvals tab, which
 * also drives the email copies); the submitter always gets the decision status + reason back.
 * Polls every 60s; the badge shows the unread count; clicking an item marks it read and
 * navigates to its link.
 */
@Component({
    selector: 'app-notification-bell',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatMenuModule, MatTooltipModule],
    templateUrl: './notification-bell.component.html',
    styleUrls: ['./notification-bell.component.scss']
})
export class NotificationBellComponent implements OnInit, OnDestroy {
    public items: INotification[] = [];
    public unreadCount = 0;
    private poll?: Subscription;

    constructor(private notifications: NotificationsService, private router: Router) { }

    public ngOnInit(): void {
        this.poll = interval(60000).pipe(
            startWith(0),
            switchMap(() => this.notifications.mine())
        ).subscribe({
            next: list => {
                this.items = list?.items ?? [];
                this.unreadCount = list?.unreadCount ?? 0;
            },
            error: () => { /* transient - the next poll retries */ }
        });
    }

    public ngOnDestroy(): void {
        this.poll?.unsubscribe();
    }

    /** Fresh fetch when the menu opens so the list is current even mid-poll-interval. */
    public refresh(): void {
        this.notifications.mine().subscribe({
            next: list => {
                this.items = list?.items ?? [];
                this.unreadCount = list?.unreadCount ?? 0;
            },
            error: () => { /* keep the last list */ }
        });
    }

    public open(notification: INotification): void {
        if (!notification.readOn) {
            notification.readOn = new Date().toISOString();
            this.unreadCount = Math.max(0, this.unreadCount - 1);
            this.notifications.markRead(notification.id).subscribe({ next: () => { }, error: () => { } });
        }
        if (notification.linkUrl) {
            this.router.navigateByUrl(notification.linkUrl);
        }
    }

    public markAllRead(event: Event): void {
        event.stopPropagation();
        this.notifications.markAllRead().subscribe({
            next: () => {
                const now = new Date().toISOString();
                this.items.forEach(i => i.readOn = i.readOn ?? now);
                this.unreadCount = 0;
            },
            error: () => { }
        });
    }

    public badgeText(): string {
        return this.unreadCount > 99 ? '99+' : `${this.unreadCount}`;
    }
}
